import PropTypes from 'prop-types';
import { RecordContextProvider, useTranslate } from 'react-admin';
import { Box, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ParameterSubsetTypeField } from '@/components/parameter/fields/ParameterSubsetTypeField.jsx';
import { ParameterSubsetTargetsField } from '@/components/parameter/fields/ParameterSubsetTargetsField.jsx';
import { ParameterSubsetValueField } from '@/components/parameter/fields/ParameterSubsetValueField.jsx';
import ActiveIcon from '@/assets/icons/active-icon.svg?react';
import DisactiveIcon from '@/assets/icons/disactive-icon.svg?react';
import { parameterSubsetTreeNodeStyles } from '@/themes/commonStyles';

const StyledTreeNode = styled(Box, { shouldForwardProp: prop => prop !== 'depth' })(({ depth }) => parameterSubsetTreeNodeStyles(depth));

const StatusFlag = ({ isOn, label }) => (
    <Box className='parameter-subset-tree-flag'>
        {isOn ? <ActiveIcon /> : <DisactiveIcon />}
        <Typography variant='body2'>{label}</Typography>
    </Box>
);

StatusFlag.propTypes = {
    isOn: PropTypes.bool,
    label: PropTypes.string.isRequired,
};

export const ParameterSubsetTreeNode = ({ subset, depth }) => {
    const translate = useTranslate();
    const children = Array.isArray(subset?.subsets) ? subset.subsets : [];
    const isInactive = subset?.isActive === false;

    return (
        <RecordContextProvider value={subset}>
            <StyledTreeNode depth={depth} className={isInactive ? 'is-inactive' : undefined}>
                <Box className='parameter-subset-tree-header'>
                    <Box className='parameter-subset-tree-scope'>
                        <Box className='parameter-subset-tree-type'>
                            <ParameterSubsetTypeField />
                        </Box>
                        <Box className='parameter-subset-tree-targets'>
                            <ParameterSubsetTargetsField />
                        </Box>
                    </Box>
                    <Box className='parameter-subset-tree-value'>
                        <Typography variant='caption'>{translate('resources.parameter.fields.value')}</Typography>
                        <ParameterSubsetValueField />
                    </Box>
                </Box>
                <Stack className='parameter-subset-tree-meta' direction='row'>
                    <StatusFlag isOn={subset?.isActive !== false} label={translate('resources.parameter.fields.isActive')} />
                    <StatusFlag isOn={Boolean(subset?.isSignificant)} label={translate('resources.parameter.fields.isSignificant')} />
                    {subset?.deactivateOthers ? <StatusFlag isOn={true} label={translate('resources.parameter.fields.deactivateOthers')} /> : null}
                </Stack>
                {children.length > 0 ? (
                    <Box className='parameter-subset-tree-children'>
                        {children.map(child => (
                            <ParameterSubsetTreeNode key={child.id} subset={child} depth={depth + 1} />
                        ))}
                    </Box>
                ) : null}
            </StyledTreeNode>
        </RecordContextProvider>
    );
};

ParameterSubsetTreeNode.propTypes = {
    subset: PropTypes.shape({
        id: PropTypes.string,
        isActive: PropTypes.bool,
        isSignificant: PropTypes.bool,
        deactivateOthers: PropTypes.bool,
        subsets: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
    depth: PropTypes.number.isRequired,
};
